import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  User,
  ChevronDown,
  UserCircle,
  Bell,
  LogOut
} from 'lucide-react';
import { toast } from 'react-toastify';

const Header = ({ user, onLogout }) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef(null);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const handleLogout = async () => {
    try {
      setIsUserMenuOpen(false);
      await onLogout();
      toast.success('Logout realizado com sucesso!');
    } catch (error) {
      console.error('Erro no logout:', error);
      toast.error('Erro ao fazer logout');
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Administrador do Sistema';
      case 'manager': return 'Gerente';
      case 'user': return 'Usuário';
      default: return 'Usuário';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-500';
      case 'manager': return 'bg-blue-500';
      case 'user': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  // Obter título da página atual
  const getPageTitle = () => {
    const titles = {
      '/dashboard': 'Dashboard',
      '/search': 'Consultas',
      '/cobranca': 'Nova Cobrança', 
      '/apis': 'Status das APIs',
      '/users': 'Gerenciar Usuários',
      '/reprocess': 'Reprocessar JSON',
      '/settings': 'Configurações'
    };
    return titles[location.pathname] || 'Painel Bitrix';
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border-b border-gray-200 shadow-sm w-full"
      style={{ zIndex: 10 }}
    >
      {/* Header Principal */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Título da Página */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h1 className="text-2xl font-bold text-gray-900">{getPageTitle()}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {location.pathname === '/dashboard' && 'Visão geral do sistema'}
              {location.pathname === '/search' && 'Pesquise por PC, CPF ou CNPJ'}
              {location.pathname === '/cobranca' && 'Criar nova cobrança no sistema'}
              {location.pathname === '/apis' && 'Monitore o status das APIs externas'}
              {location.pathname === '/users' && 'Gerencie usuários do sistema'}
              {location.pathname === '/reprocess' && 'Reprocesse arquivos JSON'}
              {location.pathname === '/settings' && 'Configurações do sistema'}
            </p>
          </motion.div>

          {/* Menu do Usuário */}
          <div className="flex items-center space-x-4">
            {/* Informações do Usuário */}
            <div className="hidden md:flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">{user?.full_name || user?.username}</p>
                <div className="flex items-center justify-end space-x-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${getRoleColor(user?.role)}`}></span>
                  <span className="text-xs text-gray-500">{getRoleLabel(user?.role)}</span>
                </div>
              </div>
            </div>

            {/* Botão do Usuário */}
            <div className="relative" ref={userMenuRef}>
              <motion.button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-all duration-200"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                title="Menu do usuário"
              >
                <User className="w-5 h-5 text-gray-600" />
                <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
              </motion.button>

              {/* Dropdown do Usuário */}
              <AnimatePresence>
                {isUserMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="user-dropdown w-56 bg-white rounded-xl shadow-2xl border border-gray-200 py-2"
                  >
                    {/* Header do usuário */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                            <UserCircle className="w-6 h-6 text-white" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {user?.full_name || user?.username}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`inline-block w-2 h-2 rounded-full ${getRoleColor(user?.role)}`}></span>
                            <span className="text-xs text-gray-600">{getRoleLabel(user?.role)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Opções do menu */}
                    <div className="py-1">
                      {/* Perfil */}
                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          navigate('/settings');
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                      >
                        <UserCircle className="w-4 h-4" />
                        <span>Meu Perfil</span>
                      </button>
                      
                      {/* Notificações */}
                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          toast.info('Sistema de notificações em desenvolvimento');
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                      >
                        <Bell className="w-4 h-4" />
                        <span>Notificações</span>
                      </button>
                      
                      {/* Separador */}
                      <div className="border-t border-gray-100 my-1"></div>
                      
                      {/* Logout */}
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center space-x-3 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sair</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Header;
